import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { canPerformAction } from '../utils/permissionUtils';

// Add mobile-specific styles
const nextRehearsalStyles = `
  .next-rehearsal-container {
    margin-top: 0;
  }
`;

const NextRehearsal = ({ refreshTrigger = 0 }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [nextRehearsal, setNextRehearsal] = useState(null);
  const [cancelledRehearsals, setCancelledRehearsals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadNextRehearsal();
  }, [refreshTrigger]);

  const loadNextRehearsal = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Load next rehearsal
      const rehearsalResponse = await fetch(`${API_BASE}/api/rehearsals?operation=getNext&timezone=${encodeURIComponent(userTimezone)}`);
      
      if (rehearsalResponse.ok) {
        const rehearsalData = await rehearsalResponse.json();
        setNextRehearsal(rehearsalData);
      } else {
        throw new Error('Failed to load rehearsal data');
      }

      // Load recent cancelled rehearsals (public information)
      try {
        // Use a public endpoint for cancelled rehearsals or pass minimal auth
        const exceptionsResponse = await fetch(`${API_BASE}/api/rehearsals?operation=getExceptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user?.id || 'public',
            userRole: user?.role || 'public'
          })
        });
          
          if (exceptionsResponse.ok) {
            const exceptionsData = await exceptionsResponse.json();
            // Filter for recently cancelled rehearsals (within last 7 days and upcoming)
            const recentCancellations = exceptionsData.filter(exception => {
              if (exception.exception_type !== 'cancelled') return false;
              const exceptionDate = new Date(exception.original_date);
              const now = new Date();
              const diffDays = (exceptionDate - now) / (1000 * 60 * 60 * 24);
              return diffDays >= -2 && diffDays <= 7; // Recent and upcoming cancellations
            });
            
            setCancelledRehearsals(recentCancellations);
          } else {
            setCancelledRehearsals([]);
          }
        } catch (exceptionsErr) {
          // Don't fail the whole component if exceptions can't be loaded
          console.error('Error loading cancelled rehearsals:', exceptionsErr);
          setCancelledRehearsals([]);
        }
    } catch (err) {
      setError(err.message);
      console.error('Error loading next rehearsal:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return t('rehearsals.today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('rehearsals.tomorrow');
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Display in 24-hour format
    return timeString;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10b981';
      case 'changed': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return '✅';
      case 'changed': return '⚠️';
      case 'cancelled': return '❌';
      default: return '📅';
    }
  };

  const handleEditCalendarNotes = (calendarNotes) => {
    // Navigate to main page with calendar edit parameters
    const entryDate = calendarNotes.date.split('T')[0]; // Convert to YYYY-MM-DD format
    const editUrl = `/?edit=${calendarNotes.id}&date=${entryDate}`;
    
    // Always navigate to the URL, this will trigger the Calendar component to handle the edit
    window.location.href = editUrl;
  };

  // Always render the component, even if no rehearsal is found
  // Don't render only if there's an error AND no data
  // console.log('NextRehearsal render state:', { loading, error, nextRehearsal });
  // console.log('NextRehearsal notes check:', nextRehearsal?.notes, nextRehearsal?.notes_html);

  return (
    <>
      {/* Inject styles */}
      <style>{nextRehearsalStyles}</style>
      

      <div 
        className="next-rehearsal-container"
        style={{
          backgroundColor: '#1f2937',
          borderRadius: '12px',
          border: '1px solid #374151',
          overflow: 'hidden',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
      >
      {/* Header - Always visible */}
      <div 
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
          color: '#ffffff',
          padding: '14px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)';
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>🎭</span>
          <h3 style={{ 
            margin: 0, 
            fontSize: '16px', 
            fontWeight: '600'
          }}>
            {t('rehearsals.nextRehearsal')}
          </h3>
          
          {/* Quick preview when collapsed - always show next rehearsal info */}
          {!expanded && nextRehearsal && !loading && (
            <>
              <span style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: '500',
                marginLeft: '8px'
              }}>
                • {formatDate(nextRehearsal.date)} at {formatTime(nextRehearsal.time)}
              </span>
              
              {/* Cancellation notice as side note */}
              {cancelledRehearsals.length > 0 && (
                <span style={{
                  marginLeft: '8px',
                  padding: '2px 6px',
                  backgroundColor: '#dc2626',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#ffffff'
                }}>
                  NOTE: {formatDate(cancelledRehearsals[0].original_date)} cancelled
                </span>
              )}
            </>
          )}
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* Status indicator when collapsed */}
          {!expanded && nextRehearsal && !loading && (
            <span style={{
              fontSize: '12px',
              color: getStatusColor(nextRehearsal.status),
              fontWeight: '600'
            }}>
              {getStatusIcon(nextRehearsal.status)}
            </span>
          )}
          
          {/* Expand/collapse arrow */}
          <div style={{ 
            color: 'rgba(255,255,255,0.8)', 
            fontSize: '14px',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{
          padding: '20px',
          backgroundColor: '#1f2937'
        }}>
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              color: '#9ca3af',
              fontSize: '14px'
            }}>
              {t('rehearsals.loadingInfo')}
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: '#991b1b',
              border: '1px solid #dc2626',
              color: '#fecaca',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && (
            <div>
              {/* Main rehearsal info - Only show if there is a next rehearsal */}
              {nextRehearsal && (
                // FIX: Added a wrapper div to contain all rehearsal details
                <div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                  {/* Date & Time */}
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #4b5563'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {t('rehearsals.when')}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#f9fafb',
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>
                      {formatDate(nextRehearsal.date)}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#e5e7eb'
                    }}>
                      {formatTime(nextRehearsal.time)}
                    </div>
                  </div>

                  {/* Location */}
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #4b5563'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {t('rehearsals.where')}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#f9fafb',
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>
                      📍 {nextRehearsal.location?.name || t('rehearsals.locationTBD')}
                    </div>
                    {nextRehearsal.location?.address && (
                      <div style={{
                        fontSize: '12px',
                        color: '#d1d5db'
                      }}>
                        {nextRehearsal.location.address}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #4b5563'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {t('rehearsals.status')}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: getStatusColor(nextRehearsal.status),
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      {getStatusIcon(nextRehearsal.status)}
                      {nextRehearsal.status?.replace('_', ' ') || t('rehearsals.statusScheduled')}
                    </div>
                  </div>
                </div>

                {/* Notes/Instructions - Only show if not empty */}
                {nextRehearsal.notes && nextRehearsal.notes.trim() && (
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #4b5563'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {t('rehearsals.notesInstructions')}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#e5e7eb',
                      lineHeight: '1.6'
                    }}>
                      {nextRehearsal.notes_html ? (
                        <div dangerouslySetInnerHTML={{ __html: nextRehearsal.notes_html }} />
                      ) : (
                        nextRehearsal.notes
                      )}
                    </div>
                  </div>
                )}

                {/* Calendar Rehearsal Notes - Only show if has any content */}
                {nextRehearsal.calendar_notes && (
                  nextRehearsal.calendar_notes.content_html ||
                  (nextRehearsal.calendar_notes.content && nextRehearsal.calendar_notes.content.trim()) ||
                  (nextRehearsal.calendar_notes.title && nextRehearsal.calendar_notes.title.trim()) ||
                  nextRehearsal.calendar_notes.audio_clips?.length > 0
                ) && (
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #4b5563',
                    marginTop: (nextRehearsal.notes && nextRehearsal.notes.trim()) ? '16px' : '0px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        fontWeight: '600'
                      }}>
                        {t('rehearsals.rehearsalNotes')}
                      </div>

                      {/* Edit button for users with calendar editing permissions */}
                      {user && canPerformAction(user, 'edit-calendar-entry') && (
                        <button
                          onClick={() => handleEditCalendarNotes(nextRehearsal.calendar_notes)}
                          style={{
                            backgroundColor: '#4b5563',
                            border: '1px solid #6b7280',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#e5e7eb',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#6b7280';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#4b5563';
                            e.currentTarget.style.borderColor = '#6b7280';
                          }}
                          title={t('rehearsals.editRehearsalNotes')}
                        >
                          ✏️ {t('common.edit')}
                        </button>
                      )}
                    </div>
                    {(nextRehearsal.calendar_notes.content_html || nextRehearsal.calendar_notes.content || nextRehearsal.calendar_notes.title) && (
                      <div style={{
                        fontSize: '16px',
                        color: '#e5e7eb',
                        lineHeight: '1.6'
                      }}>
                        {nextRehearsal.calendar_notes.content_html ? (
                          <div dangerouslySetInnerHTML={{ __html: nextRehearsal.calendar_notes.content_html }} />
                        ) : nextRehearsal.calendar_notes.content ? (
                          nextRehearsal.calendar_notes.content
                        ) : (
                          nextRehearsal.calendar_notes.title
                        )}
                      </div>
                    )}
                    
                    {/* Audio clips if they exist */}
                    {nextRehearsal.calendar_notes.audio_clips && nextRehearsal.calendar_notes.audio_clips.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #4b5563'
                      }}>
                        <div style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          fontWeight: '600'
                        }}>
                          {t('rehearsals.audioClips')}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          {nextRehearsal.calendar_notes.audio_clips.map((clip, index) => (
                            <div key={clip.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px',
                              backgroundColor: '#4b5563',
                              borderRadius: '6px'
                            }}>
                              <div style={{
                                fontSize: '16px'
                              }}>
                                🎵
                              </div>
                              <div style={{
                                flex: 1,
                                fontSize: '13px',
                                color: '#f3f4f6'
                              }}>
                                {clip.name}
                                {clip.duration && (
                                  <span style={{
                                    color: '#9ca3af',
                                    fontSize: '11px',
                                    marginLeft: '8px'
                                  }}>
                                    ({Math.floor(clip.duration / 60)}:{(clip.duration % 60).toString().padStart(2, '0')})
                                  </span>
                                )}
                              </div>
                              <audio 
                                controls 
                                style={{
                                  height: '32px',
                                  fontSize: '12px'
                                }}
                              >
                                <source src={clip.file_url} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Location Map */}
                {nextRehearsal.location?.latitude && nextRehearsal.location?.longitude && (
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #4b5563',
                    marginTop: '16px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      fontWeight: '600'
                    }}>
                      {t('rehearsals.locationMap')}
                    </div>
                    <div style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      height: '200px',
                      backgroundColor: '#4b5563'
                    }}>
                      {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                        <iframe
                          src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${nextRehearsal.location.latitude},${nextRehearsal.location.longitude}&zoom=17&maptype=roadmap`}
                          width="100%"
                          height="200"
                          style={{ border: 'none' }}
                          allowFullScreen={false}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Rehearsal Location"
                        />
                      ) : (
                        <div style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          <div style={{ marginBottom: '8px', fontSize: '24px' }}>🗺️</div>
                          <div>{t('rehearsals.mapRequiresApiKey')}</div>
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                            {t('rehearsals.coordinates')}: {nextRehearsal.location.latitude}, {nextRehearsal.location.longitude}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick actions for directors */}
                {user && (
                  <div style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid #4b5563',
                    display: 'flex',
                    gap: '8px',
                    fontSize: '12px'
                  }}>
                    {nextRehearsal.location?.maps_url && (
                      <a
                        href={nextRehearsal.location.maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        🗺️ {t('rehearsals.viewOnMap')}
                      </a>
                    )}
                    
                    {/* TODO: Add more quick actions */}
                  </div>
                )}
              </div> // FIX: This now correctly closes the wrapper div. The stray tag is gone.
              )}

              {/* Cancellation details at bottom when expanded */}
              {cancelledRehearsals.length > 0 && (
                <div style={{
                  backgroundColor: '#991b1b',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  padding: '12px',
                  marginTop: '16px'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#fecaca',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    ❌ Recently Cancelled Rehearsal
                  </div>
                  {cancelledRehearsals.map((cancellation, index) => (
                    <div key={cancellation.id} style={{
                      marginBottom: index < cancelledRehearsals.length - 1 ? '8px' : '0'
                    }}>
                      <div style={{
                        fontSize: '13px',
                        color: '#fca5a5',
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        Was scheduled: {formatDate(cancellation.original_date)} at {cancellation.original_time}
                      </div>
                      {cancellation.notes && (
                        <div style={{
                          fontSize: '12px',
                          color: '#fbb6b6',
                          fontStyle: 'italic',
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          padding: '6px 8px',
                          borderRadius: '4px'
                        }}>
                          Reason: {cancellation.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Show message if no next rehearsal but only if no cancellations either */}
              {!nextRehearsal && cancelledRehearsals.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#9ca3af',
                  fontSize: '14px'
                }}>
                  {t('rehearsals.noUpcoming')}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
    </>
  );
};

export default NextRehearsal;